import React from 'react';
import { Space, Button, Card, Select, Slider, Switch, Popconfirm, Typography, Empty } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { FocusPointPicker } from '../../../components/FocusPointPicker';

const { Text } = Typography;

interface MotionTabPanelProps {
  selectedPanel: any;
  selectedPanelIndex: number;
  selectedMotionIndex: number | null;
  setSelectedMotionIndex: (index: number | null) => void;
  handleAddMotion: (panelIndex: number) => void;
  handleEditMotion: (panelIndex: number, motionIndex: number, updates: any) => void;
  handleDeleteMotion: (panelIndex: number, motionIndex: number) => void;
}

const MotionTabPanel: React.FC<MotionTabPanelProps> = ({
  selectedPanel,
  selectedPanelIndex,
  selectedMotionIndex,
  setSelectedMotionIndex,
  handleAddMotion,
  handleEditMotion,
  handleDeleteMotion,
}) => {
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      <Button
        icon={<PlusOutlined />}
        onClick={() => handleAddMotion(selectedPanelIndex)}
        block
        size="small"
      >
        Add Motion Effect
      </Button>

      {selectedPanel.motionEffects && selectedPanel.motionEffects.length > 0 ? (
        selectedPanel.motionEffects.map((effect: any, idx: number) => (
          <Card 
            key={effect.id || idx} 
            size="small"
            id={`motion-effect-${idx}`}
            style={{ 
              fontSize: '12px',
              backgroundColor: selectedMotionIndex === idx ? '#4a4a6a' : '#3a3a3a',
              border: selectedMotionIndex === idx ? '2px solid #722ed1' : '1px solid #444',
              transition: 'all 0.2s',
            }}
            styles={{ body: { backgroundColor: selectedMotionIndex === idx ? '#4a4a6a' : '#3a3a3a' } }}
            onClick={() => setSelectedMotionIndex(idx)}
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Select
                  value={effect.type}
                  onChange={(value) => handleEditMotion(selectedPanelIndex, idx, { type: value })}
                  size="small"
                  style={{ flex: 1, marginRight: 8 }}
                  options={[
                    { label: '🔍 Zoom In', value: 'zoom-in' },
                    { label: '🔎 Zoom Out', value: 'zoom-out' },
                    { label: '⬅️ Pan Left', value: 'pan-left' },
                    { label: '➡️ Pan Right', value: 'pan-right' },
                    { label: '⬆️ Pan Up', value: 'pan-up' },
                    { label: '⬇️ Pan Down', value: 'pan-down' },
                    { label: '🎬 Ken Burns', value: 'ken-burns' },
                    { label: '🔄 Zoom In-Out', value: 'zoom-in-out' },
                    { label: '🎯 Double Zoom', value: 'double-zoom' },
                    { label: '🌊 Slow Pan', value: 'slow-pan' },
                    { label: '📱 Static', value: 'static' },
                    { label: '💨 Slide', value: 'slide' },
                    { label: '✨ Fade', value: 'fade' },
                    { label: '🔔 Shake', value: 'shake' },
                    { label: '🔄 Rotate', value: 'rotate' },
                  ]}
                />
                <Popconfirm
                  title="Delete this motion effect?"
                  onConfirm={() => handleDeleteMotion(selectedPanelIndex, idx)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </div>

              <div>
                <Text style={{ fontSize: '11px', color: '#999' }}>Start Offset (s)</Text>
                <Slider
                  min={0}
                  max={selectedPanel.endTime - selectedPanel.startTime}
                  step={0.1}
                  value={effect.startOffset}
                  onChange={(value) => handleEditMotion(selectedPanelIndex, idx, { startOffset: value })}
                  tooltip={{ formatter: (v) => `${v}s` }}
                />
              </div>

              <div>
                <Text style={{ fontSize: '11px', color: '#999' }}>Duration (s)</Text>
                <Slider
                  min={0.5}
                  max={5}
                  step={0.1}
                  value={effect.duration}
                  onChange={(value) => handleEditMotion(selectedPanelIndex, idx, { duration: value })}
                  tooltip={{ formatter: (v) => `${v}s` }}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: '11px', color: '#999' }}>Intensity</Text>
                  <Slider
                    min={0.1}
                    max={2}
                    step={0.1}
                    value={effect.intensity}
                    onChange={(value) => handleEditMotion(selectedPanelIndex, idx, { intensity: value })}
                    tooltip={{ formatter: (v) => `${v}x` }}
                  />
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Text style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Overlay</Text>
                  <Switch
                    checked={effect.overlay || false}
                    onChange={(checked) => handleEditMotion(selectedPanelIndex, idx, { overlay: checked })}
                    size="small"
                  />
                </div>
              </div>

              {selectedPanel.imageUrl && (
                <>
                  <FocusPointPicker
                    imageUrl={selectedPanel.imageUrl}
                    focusPoint={effect.focusPoint || { x: 0.5, y: 0.5 }}
                    onChange={(point) => {
                      if (effect.type === 'double-zoom') {
                        handleEditMotion(selectedPanelIndex, idx, { 
                          focusPoint: point,
                          metadata: {
                            ...effect.metadata,
                            firstFocus: point
                          }
                        });
                      } else {
                        handleEditMotion(selectedPanelIndex, idx, { focusPoint: point });
                      }
                    }}
                  />
                  
                  {effect.type === 'double-zoom' && (
                    <div style={{ marginTop: '8px' }}>
                      <Text style={{ fontSize: '11px', color: '#999' }}>
                        Second Focus Point (click on image)
                      </Text>
                      <FocusPointPicker
                        imageUrl={selectedPanel.imageUrl}
                        focusPoint={effect.metadata?.secondFocus || { x: 0.5, y: 0.5 }}
                        onChange={(point) => handleEditMotion(selectedPanelIndex, idx, { 
                          metadata: {
                            ...effect.metadata,
                            secondFocus: point
                          }
                        })}
                      />
                    </div>
                  )}
                </>
              )}

              {effect.reasoning && (
                <Text style={{ fontSize: '10px', color: '#aaa' }}>
                  💡 {effect.reasoning}
                </Text>
              )}
            </Space>
          </Card>
        ))
      ) : (
        <Empty 
          description={<Text style={{ color: '#999' }}>No motion effects</Text>}
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      )}
    </Space>
  );
};

export default MotionTabPanel;
